import { useCallback, useEffect, useState } from 'react';
import { Button, MessageBar, MessageBarBody, Spinner, Text, Title2, makeStyles } from '@fluentui/react-components';
import { tokens } from '@vigeo/ui';
import { PortalShell } from './PortalShell.js';
import { TokenExchange } from './TokenExchange.js';
import { StepRunner } from './StepRunner.js';
import { ReissueRequest } from './ReissueRequest.js';
import { fetchPacket, isPortalError, type Packet, type PortalError } from './api.js';
import { portalOnInThisBuild } from './availability.js';

const useStyles = makeStyles({
  block: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  button: { minHeight: '2.75rem', alignSelf: 'flex-start' },
});

type State =
  | { readonly kind: 'code' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'packet'; readonly packet: Packet }
  /** The server answers "not available yet" for the whole portal (#625). */
  | { readonly kind: 'unavailable' }
  /** `token`: the link the person arrived with, in memory only, for "email me a new link". */
  | { readonly kind: 'ended'; readonly error: PortalError; readonly token?: string | null };

/**
 * The "visitor" surface (ADR-004). No account, no Entra, no chrome from the HR app.
 *
 * Reached only through a scoped, expiring link plus a code stated separately. There is no
 * sign-in path here and there never will be — a candidate who cannot get in asks for a new
 * link, which is the only recovery this design has and the only one it needs.
 */
export function PortalPage() {
  const styles = useStyles();
  // A build made with the portal switched off says so at once: no code box, and no request at all.
  const [closedInThisBuild] = useState(() => !portalOnInThisBuild());
  const [state, setState] = useState<State>(closedInThisBuild ? { kind: 'unavailable' } : { kind: 'code' });
  // Read before TokenExchange strips it from the address bar (its effect runs first).
  const [arrivedWithLink] = useState(() => new URLSearchParams(window.location.search).has('t'));

  // Every way this page can be told "no" comes through here, so a closed portal (#625) is one
  // plain page whichever request found it out, and never the "we need to send you a new link" one:
  // a new link would be refused too.
  const end = useCallback((error: PortalError, token?: string | null) => {
    setState(error.unavailable ? { kind: 'unavailable' } : { kind: 'ended', error, token });
  }, []);

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    const result = await fetchPacket();
    if (isPortalError(result)) end(result);
    else setState({ kind: 'packet', packet: result });
  }, [end]);

  // A session may already be open — the person closed the tab and came back inside the
  // hour. Try once before asking for a code they would have to go and find again. Not when
  // they arrived by their link: the exchange opens the session, and a probe that can only
  // answer 401 would put a needless error line in every candidate's console.
  useEffect(() => {
    if (closedInThisBuild) {
      // TokenExchange is what takes a link out of the address bar, and it is never shown here. A
      // link that may still work must not sit in the address bar or in browser history.
      if (arrivedWithLink) window.history.replaceState(null, '', '/portal');
      return;
    }
    if (arrivedWithLink) return;
    void (async () => {
      const result = await fetchPacket();
      if (!isPortalError(result)) setState({ kind: 'packet', packet: result });
      // Any other refusal means "no session yet" and leaves the code form up. This one means no code will work.
      else if (result.unavailable) setState({ kind: 'unavailable' });
    })();
  }, [arrivedWithLink, closedInThisBuild]);

  if (state.kind === 'unavailable') {
    return (
      <PortalShell>
        <div className={styles.block}>
          <Title2 as="h1">This is not available yet</Title2>
          <Text>
            Online onboarding is not open yet. You do not need to do anything here. If you were
            expecting to use it, please contact our office.
          </Text>
        </div>
      </PortalShell>
    );
  }

  if (state.kind === 'loading') {
    return (
      <PortalShell>
        <Spinner size="small" label="Loading your onboarding" labelPosition="after" />
      </PortalShell>
    );
  }

  if (state.kind === 'ended') {
    return (
      <PortalShell>
        <div className={styles.block}>
          <Title2 as="h1">We need to send you a new link</Title2>
          {/* The API's own words. Never reworded here, and never says whether the link or
              the code was the problem. */}
          <MessageBar intent="warning">
            <MessageBarBody>{state.error.message}</MessageBarBody>
          </MessageBar>
          {/* A 429 is a link locked by wrong codes, wherever it surfaced — HR's to replace. */}
          {state.error.canReissue && state.error.status !== 429 ? (
            <ReissueRequest token={state.token ?? null} />
          ) : (
            <Text>
              Anything you had already typed is still saved. Contact our office and we will send
              a new link to the address we have for you.
            </Text>
          )}
        </div>
      </PortalShell>
    );
  }

  if (state.kind === 'packet') {
    const { packet } = state;
    return (
      <PortalShell>
        <div className={styles.block}>
          <Title2 as="h1">Hello {packet.name}</Title2>
          <Text>
            {packet.steps.length === 0
              ? 'There is nothing for you to do right now.'
              : 'Work through these in any order. Everything saves as you go.'}
          </Text>
        </div>
        <StepRunner steps={packet.steps} onExpired={(error) => end(error)} />
      </PortalShell>
    );
  }

  return (
    <PortalShell>
      <TokenExchange onOpened={() => void load()} onUnrecoverable={end} />
      <Button className={styles.button} appearance="transparent" onClick={() => void load()}>
        I already entered my code
      </Button>
    </PortalShell>
  );
}
